import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { getPricingPlans, getOmisePublicKey, createOmiseToken, createPromptPayToken, createSubscription, PricingPlan } from '../../services/omiseService';
import { CreditCard, Lock, CheckCircle, AlertCircle, ArrowLeft, Loader2, Calendar, Shield, Smartphone, QrCode, Download } from 'lucide-react';
import SEO from '../../components/SEO';
import { auth } from '../../lib/firebase';

export default function OmiseSubscriptionCheckout() {
  const [searchParams] = useSearchParams();
  const tier = searchParams.get('tier') as 'pro_monthly' | 'pro_yearly' | 'pro_plus_monthly' | 'pro_plus_yearly';
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [publicKey, setPublicKey] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'promptpay'>('card');
  const [promptPayProcessing, setPromptPayProcessing] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [sourceId, setSourceId] = useState<string>('');

  const [cardData, setCardData] = useState({
    name: '',
    number: '',
    expiration_month: '',
    expiration_year: '',
    security_code: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/subscription/checkout?tier=' + tier);
      return;
    }

    loadData();
  }, [user, tier, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plansData, key] = await Promise.all([
        getPricingPlans(),
        getOmisePublicKey(),
      ]);

      setPlans(plansData);
      setPublicKey(key);

      const plan = plansData.find(p => p.tier === tier);
      if (plan) {
        setSelectedPlan(plan);
      } else {
        setError('ไม่พบแพ็กเกจที่เลือก');
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError('ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (paymentMethod === 'promptpay' && selectedPlan && publicKey && !qrCodeUrl) {
      generatePromptPayQR();
    }
  }, [paymentMethod, selectedPlan, publicKey]);

  const generatePromptPayQR = async () => {
    if (!selectedPlan) {
      setError('ข้อมูลไม่ครบถ้วน');
      return;
    }

    setPromptPayProcessing(true);
    setError('');

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const token = await user.getIdToken();
      
      console.log('Creating PromptPay QR via backend...');
      const response = await fetch('https://createpromptpayqr-mgs3rglu4q-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: selectedPlan.price * 100, // Convert to satangs
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create QR code');
      }

      const data = await response.json();
      console.log('PromptPay QR created:', data);

      if (data.qr_code_url && data.source_id) {
        setQrCodeUrl(data.qr_code_url);
        setSourceId(data.source_id);
      } else {
        setError('ไม่สามารถสร้าง QR Code ได้');
      }
    } catch (err: any) {
      console.error('PromptPay QR generation error:', err);
      setError(err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setPromptPayProcessing(false);
    }
  };

  const handlePromptPayPayment = async () => {
    if (!sourceId || !selectedPlan) {
      setError('กรุณารอ QR Code โหลดเสร็จก่อน');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      console.log('Creating subscription with source:', sourceId);
      const result = await createSubscription(sourceId, selectedPlan.plan_id, selectedPlan.billing_cycle);
      console.log('Subscription result:', result);

      if (result.authorize_uri) {
        window.location.href = result.authorize_uri;
        return;
      }

      if (result.status === 'successful' && result.charge_id) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/payment/omise/success?charge_id=${result.charge_id}&type=subscription`);
        }, 1500);
      } else {
        setError('การชำระเงินไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
      }
    } catch (err: any) {
      console.error('PromptPay payment error:', err);
      setError(err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveQRCode = () => {
    if (!qrCodeUrl) return;
    
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `promptpay-qr-${selectedPlan?.plan_id || 'payment'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPlan || !publicKey) {
      setError('ข้อมูลไม่ครบถ้วน');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      console.log('Creating Omise card token...');
      const token = await createOmiseToken(publicKey, cardData);
      console.log('Card token created:', token);

      console.log('Creating subscription...');
      const result = await createSubscription(token, selectedPlan.plan_id, selectedPlan.billing_cycle);
      console.log('Subscription result:', result);

      if (result.authorize_uri) {
        window.location.href = result.authorize_uri;
        return;
      }

      if (result.status === 'successful' && result.charge_id) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/payment/omise/success?charge_id=${result.charge_id}&type=subscription`);
        }, 1500);
      } else {
        setError('การชำระเงินไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
      }
    } catch (err: any) {
      console.error('Card payment error:', err);
      setError(err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setProcessing(false);
    }
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const chunks = cleaned.match(/.{1,4}/g) || [];
    return chunks.join(' ');
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    if (value.length <= 16 && /^\d*$/.test(value)) {
      setCardData({ ...cardData, number: value });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">ชำระเงินสำเร็จ!</h2>
          <p className="text-gray-600 mb-4">
            การสมัครสมาชิกของคุณเสร็จสมบูรณ์แล้ว
          </p>
          <p className="text-sm text-gray-500">กำลังเปลี่ยนเส้นทาง...</p>
        </div>
      </div>
    );
  }

  if (!selectedPlan) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-xl text-gray-900 mb-4">{error || 'ไม่พบแพ็กเกจที่เลือก'}</p>
          <button
            onClick={() => navigate('/pricing')}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all"
          >
            กลับไปเลือกแพ็กเกจ
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`ชำระเงิน ${selectedPlan.name} - EduMi`}
        description={`ชำระเงินสำหรับแพ็กเกจ ${selectedPlan.name} ราคา ${selectedPlan.price} บาท`}
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>ย้อนกลับ</span>
          </button>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 text-center">
            ชำระเงิน {selectedPlan.name}
          </h1>
          <p className="text-center text-gray-600 mb-8">เลือกวิธีการชำระเงินที่สะดวกสำหรับคุณ</p>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">เลือกวิธีการชำระเงิน</h2>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        paymentMethod === 'card'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      <CreditCard className={`w-8 h-8 mx-auto mb-2 ${
                        paymentMethod === 'card' ? 'text-blue-600' : 'text-gray-400'
                      }`} />
                      <p className={`font-semibold ${
                        paymentMethod === 'card' ? 'text-blue-900' : 'text-gray-600'
                      }`}>บัตรเครดิต/เดบิต</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('promptpay')}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        paymentMethod === 'promptpay'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      <QrCode className={`w-8 h-8 mx-auto mb-2 ${
                        paymentMethod === 'promptpay' ? 'text-blue-600' : 'text-gray-400'
                      }`} />
                      <p className={`font-semibold ${
                        paymentMethod === 'promptpay' ? 'text-blue-900' : 'text-gray-600'
                      }`}>PromptPay</p>
                    </button>
                  </div>
                </div>

                {paymentMethod === 'card' ? (
                <form onSubmit={handleCardSubmit}>
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <CreditCard className="w-6 h-6 text-blue-600" />
                      <h2 className="text-xl font-bold text-gray-900">ข้อมูลบัตร</h2>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          ชื่อบนบัตร <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={cardData.name}
                          onChange={(e) => setCardData({ ...cardData, name: e.target.value })}
                          placeholder="JOHN DOE"
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors uppercase"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          หมายเลขบัตร <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formatCardNumber(cardData.number)}
                          onChange={handleCardNumberChange}
                          placeholder="1234 5678 9012 3456"
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors font-mono"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            เดือน <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={cardData.expiration_month}
                            onChange={(e) => setCardData({ ...cardData, expiration_month: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                            required
                          >
                            <option value="">MM</option>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                              <option key={month} value={month.toString().padStart(2, '0')}>
                                {month.toString().padStart(2, '0')}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            ปี <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={cardData.expiration_year}
                            onChange={(e) => setCardData({ ...cardData, expiration_year: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                            required
                          >
                            <option value="">YY</option>
                            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map(year => (
                              <option key={year} value={year.toString().slice(-2)}>
                                {year.toString().slice(-2)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            CVV <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={cardData.security_code}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.length <= 4 && /^\d*$/.test(value)) {
                                setCardData({ ...cardData, security_code: value });
                              }
                            }}
                            placeholder="123"
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors font-mono"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}

                  <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl flex items-start gap-3">
                    <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">การชำระเงินปลอดภัย</p>
                      <p>ข้อมูลบัตรของคุณได้รับการเข้ารหัสและประมวลผลโดย Omise (Opn Payments) อย่างปลอดภัย</p>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={processing}
                    className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        กำลังดำเนินการ...
                      </>
                    ) : (
                      <>
                        <Lock className="w-5 h-5" />
                        ชำระเงิน ฿{selectedPlan.price.toLocaleString()}
                      </>
                    )}
                  </button>
                </form>
                ) : (
                <div>
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <QrCode className="w-6 h-6 text-blue-600" />
                      <h2 className="text-xl font-bold text-gray-900">ชำระผ่าน PromptPay</h2>
                    </div>

                    {promptPayProcessing ? (
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 text-center">
                        <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
                        <h3 className="text-lg font-bold text-gray-900 mb-2">กำลังสร้าง QR Code...</h3>
                        <p className="text-gray-600">กรุณารอสักครู่</p>
                      </div>
                    ) : qrCodeUrl ? (
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6">
                        <div className="bg-white rounded-xl p-6 mb-4 text-center">
                          <img 
                            src={qrCodeUrl} 
                            alt="PromptPay QR Code" 
                            className="w-64 h-64 mx-auto mb-4"
                          />
                          <div className="bg-blue-50 rounded-lg p-4 inline-block mb-4">
                            <p className="text-sm text-gray-600 mb-1">จำนวนเงิน</p>
                            <p className="text-3xl font-bold text-blue-600">฿{selectedPlan.price.toLocaleString()}</p>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">สแกน QR Code นี้ด้วยแอปธนาคารของคุณ</p>
                          <p className="text-xs text-gray-500">QR Code นี้ใช้ได้ภายใน 15 นาที</p>
                        </div>

                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={handleSaveQRCode}
                            className="flex-1 px-6 py-3 bg-white border-2 border-blue-600 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                          >
                            <Download className="w-5 h-5" />
                            Save QR Code
                          </button>
                          <button
                            type="button"
                            onClick={handlePromptPayPayment}
                            disabled={processing}
                            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {processing ? (
                              <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                กำลังดำเนินการ...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-5 h-5" />
                                ชำระเงินแล้ว
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 text-center">
                        <Smartphone className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-gray-900 mb-2">เกิดข้อผิดพลาด</h3>
                        <p className="text-gray-600 mb-4">ไม่สามารถสร้าง QR Code ได้</p>
                        <button
                          type="button"
                          onClick={generatePromptPayQR}
                          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all"
                        >
                          ลองใหม่อีกครั้ง
                        </button>
                      </div>
                    )}
                  </div>

                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}

                  <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl flex items-start gap-3">
                    <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">การชำระเงินปลอดภัย</p>
                      <p>ชำระเงินผ่าน PromptPay โดย Omise (Opn Payments) อย่างปลอดภัย</p>
                      <p className="mt-2 text-xs">หลังจากชำระเงินเรียบร้อยแล้ว กรุณากดปุ่ม "ชำระเงินแล้ว" เพื่อยืนยัน</p>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">สรุปการสั่งซื้อ</h3>
                
                <div className="space-y-3 mb-4 pb-4 border-b">
                  <div className="flex justify-between">
                    <span className="text-gray-600">แพ็กเกจ</span>
                    <span className="font-semibold text-gray-900">{selectedPlan.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">รอบบิล</span>
                    <span className="font-semibold text-gray-900">
                      {selectedPlan.billing_cycle === 'yearly' ? 'รายปี' : 'รายเดือน'}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center mb-6">
                  <span className="text-lg font-bold text-gray-900">ยอดรวม</span>
                  <span className="text-2xl font-bold text-blue-600">
                    ฿{selectedPlan.price.toLocaleString()}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>พื้นที่จัดเก็บ {selectedPlan.features.storage_gb} GB</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>นักเรียนไม่จำกัด</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>คอร์สไม่จำกัด</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Dashboard และ Analytics</span>
                  </div>
                  {selectedPlan.features.seo_optimization && (
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>SEO Optimization</span>
                    </div>
                  )}
                  {selectedPlan.features.sale_page_builder && (
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Sale Page Builder</span>
                    </div>
                  )}
                </div>

                {selectedPlan.billing_cycle === 'yearly' && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="text-sm text-green-800 font-semibold">
                      ประหยัด 2 เดือน!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
